import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Languages, CalendarRange, ShieldCheck, UserRound, Sparkles, Info } from "lucide-react";
import { RIVIERA_HERO, RIVIERA_CITIES, RIVIERA_SERVICES, VILLA_INTERIOR } from "@/lib/riviera";
import { RivieraHero, CityCard, BTN_AZURE, BTN_GHOST, EYEBROW_AZ, H2 } from "@/components/riviera/RivieraUI";

const WHY_ICONS = [Languages, CalendarRange, ShieldCheck, UserRound];

export default function RivieraHome() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  return (
    <div data-testid="riviera-home">
      <RivieraHero img={RIVIERA_HERO} eyebrow={t("riviera.eyebrow")}
        title={<>{t("riviera.hero_title_a")} <span className="italic text-sky-300">{t("riviera.hero_title_b")}</span></>}
        sub={t("riviera.hero_sub")}
        badge={<span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200" data-testid="riviera-quote-badge"><Info className="w-3.5 h-3.5" /> {t("riviera.badge")}</span>}>
        <Link to="/fr/book" className={BTN_AZURE} data-testid="riviera-hero-cta">{t("riviera.cta_book")}</Link>
        <Link to="/fr/inquiry" className={BTN_GHOST} data-testid="riviera-hero-quote">{t("riviera.cta_inquiry")}</Link>
      </RivieraHero>

      <section id="cities" className="py-20 md:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className={EYEBROW_AZ}>{t("riviera.cities_eyebrow")}</p>
          <h2 className={H2}>{t("riviera.cities_title")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-6 gap-5">
            {RIVIERA_CITIES.map((c, i) => (
              <div key={c.slug} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}><CityCard t={t} city={c} i={i} /></div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="py-20 md:py-28 bg-[#F0F9FF] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><p className={EYEBROW_AZ}>{t("riviera.how_eyebrow")}</p><h2 className={H2}>{t("riviera.how_title")}</h2></div>
          <div className="lg:col-span-8 grid md:grid-cols-3 gap-5">
            {(t("riviera.how", { returnObjects: true }) || []).map((s, i) => (
              <div key={i} className="rounded-2xl p-6 bg-white ring-1 ring-sky-100" data-testid={`riviera-how-${i + 1}`}>
                <div className="font-display text-5xl text-[#0EA5E9]/30">0{i + 1}</div>
                <h3 className="mt-3 text-lg font-bold text-[#0F172A]">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="py-20 md:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><p className={EYEBROW_AZ}>{t("riviera.services_eyebrow")}</p><h2 className={H2}>{t("riviera.services_title")}</h2></div>
            <p className="text-sm text-slate-500 max-w-sm" data-testid="riviera-no-price">{t("riviera.no_price")}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {RIVIERA_SERVICES.map((s) => (
              <div key={s.key} className={`rounded-2xl p-5 ring-1 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-md ${s.key === "villa" ? "bg-[#1B3A6B] text-white ring-[#1B3A6B]" : "bg-white ring-slate-200"}`} data-testid={`riviera-service-${s.key}`}>
                <Sparkles className={`w-5 h-5 ${s.key === "villa" ? "text-sky-300" : "text-[#0EA5E9]"}`} />
                <h3 className="mt-3 font-bold text-sm">{t(`riviera.services_list.${s.key}`)}</h3>
                <p className={`mt-1 text-xs leading-relaxed ${s.key === "villa" ? "text-slate-300" : "text-slate-600"}`}>{t(`riviera.services_desc.${s.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <img src={VILLA_INTERIOR} alt="Riviera villa interior" className="rounded-3xl w-full h-[420px] object-cover shadow-[0_12px_40px_-4px_rgba(27,58,107,0.15)]" />
          <div>
            <p className={EYEBROW_AZ}>{t("riviera.why_eyebrow")}</p>
            <h2 className={H2}>{t("riviera.why_title")}</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {(t("riviera.why", { returnObjects: true }) || []).map((w, i) => {
                const Icon = WHY_ICONS[i] || Sparkles;
                return (
                  <div key={i}>
                    <Icon className="w-6 h-6 text-[#0EA5E9]" />
                    <h4 className="mt-2 font-bold text-[#0F172A]">{w.t}</h4>
                    <p className="mt-1 text-sm text-slate-600">{w.d}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/fr/book" className={BTN_AZURE} data-testid="riviera-why-cta">{t("riviera.cta_book")}</Link>
              <Link to="/fr/inquiry" className="inline-flex items-center justify-center px-6 h-12 rounded-full font-semibold text-[#1B3A6B] border-2 border-[#1B3A6B]/30 hover:border-[#1B3A6B] transition-colors" data-testid="riviera-why-quote">{t("riviera.cta_inquiry")}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
